import * as React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { AuthInput } from '@/components/common/AuthInput';
import { Button } from '@/components/ui/Button';
import { AuthLink } from '@/features/auth/components/AuthLink';
import clsx from 'clsx';

const schema = z.object({
  email: z.string().email('Please enter a valid email'),
});

type FormValues = z.infer<typeof schema>;

interface ForgotPasswordFormProps {
  onSubmit: (email: string) => void;
  onRegister: () => void;
  isLoading?: boolean;
}

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  onSubmit,
  onRegister,
  isLoading = false,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  return (
    <div className='flex w-full max-w-screen-fold flex-col items-center overflow-hidden text-center'>
      {/* Header */}
      <h2
        className={clsx(
          'font-proxima font-bold leading-25 tracking-normal',
          // Mobile: 30px, White, Center
          'mb-[14px] text-center text-[30px] text-white',
          // Desktop: 38px, Brand Dark, Bottom Margin 53px (Preserved)
          'fold:mb-[53px] fold:text-[38px] fold:text-brand-800'
        )}
      >
        Forgot Password
      </h2>

      {/* Subtitle */}
      <p
        className={clsx(
          'font-sans font-normal leading-21 tracking-normal',
          // Mobile: 14px, White, Spacing (11px top, 20px bottom)
          'mb-[39px] mt-0 text-center text-14 text-white',
          // Desktop: 22px, Brand Lighter, Reset spacing
          'fold:my-0 fold:text-[22px] fold:text-brand-750'
        )}
      >
        Enter the email address associated with your account.
      </p>

      {/* Form */}
      <form onSubmit={handleSubmit((data) => onSubmit(data.email))} className='w-full'>
        {/* Input Container with specific margins */}
        <div className='mx-auto mb-[73px] mt-0 w-full max-w-[481px] text-left fold:mb-[40px] fold:mt-[36px] md:mb-[69px] md:mt-[66px]'>
          <AuthInput
            id='email'
            type='email'
            label='Email'
            placeholder='Enter your email'
            autoComplete='email'
            error={errors.email?.message}
            {...register('email')}
          />
        </div>

        {/* Submit Button */}
        <div className='mb-[36px] flex w-full justify-center'>
          <Button
            type='submit'
            variant='purple-dark'
            className={clsx(
              'h-[54px] w-full max-w-[385px]',
              'rounded-30 text-16 font-bold uppercase tracking-wide'
            )}
            disabled={isLoading}
            isLoading={isLoading}
          >
            SUBMIT THE REQUEST
          </Button>
        </div>

        {/* Footer */}
        <div className='text-center'>
          <AuthLink
            text="Don't have an account?"
            actionText='Create Account'
            onClick={onRegister}
            disabled={isLoading}
            variant='light'
          />
        </div>
      </form>
    </div>
  );
};
